import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaCopy, FaCheck, FaClock, FaMobileAlt, FaNetworkWired, FaShieldAlt, FaTag } from 'react-icons/fa';

// Model to Friendly Name mapping
const modelToFriendlyName = {
  "google Pixel 8a": "Google Pixel 8a",
  "ZTE K83V": "K83V",
  "ZTE K88": "K88",
  "ZTE K92": "K92",
  "samsung SM-G970U": "Samsung Galaxy S10e",
  "samsung SM-G970U1": "Samsung Galaxy S10e",
  "samsung SM-G970F": "Samsung Galaxy S10e",
  "samsung SM-G970W": "Samsung Galaxy S10e",
  "samsung SM-S901U": "Samsung Galaxy S22",
  "samsung SM-S901U1": "Samsung Galaxy S22",
  "samsung SM-S901E": "Samsung Galaxy S22",
  "samsung SM-S901W": "Samsung Galaxy S22",
  "samsung SM-G960U": "Samsung Galaxy S9",
  "samsung SM-G960U1": "Samsung Galaxy S9",
  "samsung SM-G965U1": "Samsung Galaxy S9+",
  "samsung SM-G965U": "Samsung Galaxy S9+",
  "samsung SM-G715U1": "Samsung Galaxy XCover Pro",
  "Lenovo Lenovo TB-X6C6L": "Lenovo K10 Tab",
  "samsung SM-X218U": "Samsung Galaxy Tab A9+",
  "samsung SM-X818U": "Samsung Galaxy Tab S9+",
  "samsung SM-X828U": "Samsung Galaxy Tab S10+"
};

const getFriendlyName = (model) => {
  if (!model) return "-";
  if (modelToFriendlyName[model]) {
    return modelToFriendlyName[model];
  }
  const lowerModel = model.toLowerCase();
  for (const [key, value] of Object.entries(modelToFriendlyName)) {
    if (key.toLowerCase() === lowerModel) {
      return value;
    }
  }
  return model;
};

export default function UemDeviceModal({ uuid, deviceFriendlyName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deviceData, setDeviceData] = useState(null);
  const [copiedField, setCopiedField] = useState(null);

  const API_BASE = process.env.REACT_APP_API_SERVER_URL;

  // Helper function to copy to clipboard
  const copyToClipboard = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
    });
  };

  // Helper function to format relative time
  const formatRelativeTime = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleString();
  };

  // Helper function to get last seen color
  const getLastSeenColor = (dateString) => {
    if (!dateString) return 'text-gray-400';
    const date = new Date(dateString);
    const now = new Date();
    const diffHours = (now - date) / 3600000;
    if (diffHours < 4) return 'text-green-600';
    if (diffHours < 24) return 'text-yellow-600';
    return 'text-red-600';
  };

  useEffect(() => {
    if (!uuid) {
      setError("UUID is required");
      setLoading(false);
      return;
    }

    const fetchDeviceInfo = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await axios.get(`${API_BASE}/maiApi/uem/device/${uuid}`);
        if (response.data.success) {
          setDeviceData(response.data.data);
        } else {
          setError(response.data.error || "Failed to fetch device information");
        }
      } catch (err) {
        console.error("Error fetching UEM device info:", err);
        setError(err.response?.data?.error || err.message || "Failed to fetch device information");
      } finally {
        setLoading(false);
      }
    };

    fetchDeviceInfo();
  }, [uuid, API_BASE]);

  if (!uuid) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-blue-600 text-white p-4 rounded-t-lg flex justify-between items-center">
          <h2 className="text-xl font-bold">Device Information</h2>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {loading && (
            <div className="text-center py-8">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading device information...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-red-800 font-semibold mb-2">Error</h3>
              <p className="text-red-600">{error}</p>
            </div>
          )}

          {deviceData && !loading && !error && (
            <div className="space-y-6">
              {/* Device Friendly Name with Last Seen */}
              {deviceFriendlyName && (
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 border-2 border-blue-300 rounded-lg p-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex-1">
                      <div className="text-sm text-blue-600 font-medium mb-1">Device Friendly Name</div>
                      <div className="text-2xl font-bold text-blue-900">{deviceFriendlyName}</div>
                    </div>
                    {deviceData.lastSeen && (
                      <div className="flex items-center gap-2">
                        <FaClock className="text-blue-600" />
                        <div>
                          <div className="text-xs text-blue-600 font-medium">Last Seen</div>
                          <div className={`text-sm font-semibold ${getLastSeenColor(deviceData.lastSeen)}`} title={new Date(deviceData.lastSeen).toLocaleString()}>
                            {formatRelativeTime(deviceData.lastSeen)}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Device Identity Section */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <FaMobileAlt className="text-blue-600" />
                    Device Identity
                  </h3>
                  {deviceFriendlyName && (
                    <button
                      onClick={() => {
                        const uemUrl = `https://cn927.awmdm.com/AirWatch/#/AirWatch/Search?query=${encodeURIComponent(deviceFriendlyName)}`;
                        window.open(uemUrl, '_blank', 'noopener,noreferrer');
                      }}
                      className="text-[10px] px-1.5 py-0.5 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
                      title="Open device in UEM portal"
                    >
                      More Info
                    </button>
                  )}
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Serial Number */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 font-medium mb-1">Serial Number</div>
                    <div className="flex items-center gap-2">
                      <div className="text-base font-semibold text-gray-900 font-mono flex-1">
                        {deviceData.serialNumber || <span className="text-gray-400">Not available</span>}
                      </div>
                      {deviceData.serialNumber && (
                        <button
                          onClick={() => copyToClipboard(deviceData.serialNumber, 'serial')}
                          className="text-gray-500 hover:text-blue-600 transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedField === 'serial' ? <FaCheck className="text-green-600" /> : <FaCopy />}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* IMEI */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 font-medium mb-1">IMEI</div>
                    <div className="flex items-center gap-2">
                      <div className="text-base font-semibold text-gray-900 font-mono flex-1">
                        {deviceData.imei || <span className="text-gray-400">Not available</span>}
                      </div>
                      {deviceData.imei && (
                        <button
                          onClick={() => copyToClipboard(deviceData.imei, 'imei')}
                          className="text-gray-500 hover:text-blue-600 transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedField === 'imei' ? <FaCheck className="text-green-600" /> : <FaCopy />}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Device Specifications Section */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <FaMobileAlt className="text-blue-600" />
                  Device Specifications
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                  {/* Platform */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 font-medium mb-1">Platform</div>
                    <div className="text-base font-semibold text-gray-900">
                      {deviceData.platform || <span className="text-gray-400">Not available</span>}
                    </div>
                  </div>

                  {/* OS */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 font-medium mb-1">OS</div>
                    <div className="text-base font-semibold text-gray-900">
                      {deviceData.os || deviceData.operatingSystem || <span className="text-gray-400">Not available</span>}
                    </div>
                  </div>

                  {/* Model */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 font-medium mb-1">Model</div>
                    <div className="text-base font-semibold text-gray-900">
                      {deviceData.model ? getFriendlyName(deviceData.model) : <span className="text-gray-400">Not available</span>}
                    </div>
                  </div>

                  {/* Phone Number */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 font-medium mb-1">Phone</div>
                    <div className="text-base font-semibold text-gray-900">
                      {deviceData.phoneNumber || <span className="text-gray-400">Not available</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Tags Section */}
              {deviceData.tags && (Array.isArray(deviceData.tags) ? deviceData.tags.length > 0 : deviceData.tags) && (
                <div className="space-y-3">
                  <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                    <FaTag className="text-blue-600" />
                    Tags
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {Array.isArray(deviceData.tags) ? (
                      deviceData.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium border border-blue-200"
                        >
                          {tag}
                        </span>
                      ))
                    ) : (
                      <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium border border-blue-200">
                        {deviceData.tags}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Network & Connectivity Section */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <FaNetworkWired className="text-blue-600" />
                  Network & Connectivity
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Home Carrier */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 font-medium mb-1">Home Carrier</div>
                    <div className="text-base font-semibold text-gray-900">
                      {deviceData.homeCarrier || deviceData.carrierName || <span className="text-gray-400">Not available</span>}
                    </div>
                  </div>

                  {/* Current Carrier */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 font-medium mb-1">Current Carrier</div>
                    <div className="text-base font-semibold text-gray-900">
                      {deviceData.currentCarrier || deviceData.carrierName || <span className="text-gray-400">Not available</span>}
                    </div>
                  </div>

                  {/* Wi-Fi IP Address */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 font-medium mb-1">Wi-Fi IP Address</div>
                    <div className="flex items-center gap-2">
                      <div className="text-base font-semibold text-gray-900 font-mono flex-1">
                        {deviceData.wifiIpAddress || <span className="text-gray-400">Not available</span>}
                      </div>
                      {deviceData.wifiIpAddress && (
                        <button
                          onClick={() => copyToClipboard(deviceData.wifiIpAddress, 'wifiIp')}
                          className="text-gray-500 hover:text-blue-600 transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedField === 'wifiIp' ? <FaCheck className="text-green-600" /> : <FaCopy />}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Wi-Fi MAC Address */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 font-medium mb-1">Wi-Fi MAC Address</div>
                    <div className="flex items-center gap-2">
                      <div className="text-base font-semibold text-gray-900 font-mono flex-1">
                        {deviceData.wifiMacAddress || <span className="text-gray-400">Not available</span>}
                      </div>
                      {deviceData.wifiMacAddress && (
                        <button
                          onClick={() => copyToClipboard(deviceData.wifiMacAddress, 'wifiMac')}
                          className="text-gray-500 hover:text-blue-600 transition-colors"
                          title="Copy to clipboard"
                        >
                          {copiedField === 'wifiMac' ? <FaCheck className="text-green-600" /> : <FaCopy />}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Wi-Fi SSID */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 font-medium mb-1">Wi-Fi SSID</div>
                    <div className="text-base font-semibold text-gray-900">
                      {deviceData.wifiSsid || <span className="text-gray-400">Not available</span>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Management Section */}
              <div className="space-y-3">
                <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                  <FaShieldAlt className="text-blue-600" />
                  Management
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Organization Group */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 font-medium mb-1">Organization Group</div>
                    <div className="text-base font-semibold text-gray-900">
                      {deviceData.organizationGroup || deviceData.locationGroupName || <span className="text-gray-400">Not available</span>}
                    </div>
                  </div>

                  {/* Android Management */}
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 font-medium mb-1">Android Management</div>
                    <div className={`text-base font-semibold ${
                      deviceData.androidManagement === 'Managed' ? 'text-green-600' : 'text-gray-900'
                    }`}>
                      {deviceData.androidManagement || <span className="text-gray-400">Not available</span>}
                    </div>
                  </div>

                  {/* Enrollment Status */}
                  {deviceData.enrollmentStatus && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="text-sm text-gray-600 font-medium mb-1">Enrollment Status</div>
                      <div>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                          deviceData.enrollmentStatus === 'Enrolled' 
                            ? 'bg-green-100 text-green-800 border border-green-300' 
                            : 'bg-red-100 text-red-800 border border-red-300'
                        }`}>
                          {deviceData.enrollmentStatus}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Compliance Status */}
                  {deviceData.complianceStatus && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="text-sm text-gray-600 font-medium mb-1">Compliance Status</div>
                      <div>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${
                          deviceData.complianceStatus === 'Compliant' 
                            ? 'bg-green-100 text-green-800 border border-green-300' 
                            : 'bg-yellow-100 text-yellow-800 border border-yellow-300'
                        }`}>
                          {deviceData.complianceStatus}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Last Enrolled On */}
                  {deviceData.lastEnrolledOn && (
                    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                      <div className="text-sm text-gray-600 font-medium mb-1">Last Enrolled On</div>
                      <div className="text-base font-semibold text-gray-900">
                        {new Date(deviceData.lastEnrolledOn).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Information */}
              {deviceData.deviceReportedName && (
                <div className="pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Additional Information</h3>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                    <div className="text-sm text-gray-600 font-medium mb-1">Device Reported Name</div>
                    <div className="text-base font-semibold text-gray-900 font-mono break-all">
                      {deviceData.deviceReportedName}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

